import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabase";
import { useOrg } from "../auth/OrgProvider";
import { Card, CardHeader } from "../ui/Card";
import type { Lead } from "@lawnops/shared";

const STATUS_COLORS: Record<Lead["status"], string> = {
  new: "bg-blue-100 text-blue-800",
  contacted: "bg-amber-100 text-amber-800",
  quoted: "bg-purple-100 text-purple-800",
  accepted: "bg-emerald-100 text-emerald-800",
  lost: "bg-slate-200 text-slate-600",
};

export function LeadsPage() {
  const { t } = useTranslation("leads");
  const { activeOrgId } = useOrg();
  const { data, isLoading, error } = useQuery({
    queryKey: ["leads", activeOrgId],
    enabled: Boolean(supabase && activeOrgId),
    queryFn: async (): Promise<Lead[]> => {
      if (!supabase || !activeOrgId) return [];
      const { data, error } = await supabase
        .from("leads")
        .select("*")
        .eq("organization_id", activeOrgId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Lead[];
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{t("title")}</h1>
          <p className="text-sm text-slate-500">
            {t("subtitle")}{" "}
            <Link to="/quote" className="text-brand-700 underline" target="_blank">
              /quote
            </Link>
            .
          </p>
        </div>
      </div>

      <Card>
        <CardHeader title={t("pipelineTitle")} />
        {!supabase ? (
          <EmptyState message={t("supabaseEmpty")} />
        ) : isLoading ? (
          <EmptyState message={t("loading")} />
        ) : error ? (
          <EmptyState message={`${t("errorPrefix")}${(error as Error).message}`} />
        ) : data && data.length > 0 ? (
          <ul className="divide-y divide-slate-100">
            {data.map((lead) => (
              <li key={lead.id} className="flex items-start justify-between py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">
                    {lead.name ?? t("unknown")}
                  </p>
                  <p className="truncate text-xs text-slate-500">
                    {lead.email ?? lead.phone ?? "—"}
                    {lead.address_line1 ? ` · ${lead.address_line1}` : ""}
                  </p>
                  {lead.lawn_area_sqft ? (
                    <p className="text-xs text-slate-400">
                      {t("areaSqft", { count: lead.lawn_area_sqft })}
                    </p>
                  ) : null}
                </div>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[lead.status]}`}
                >
                  {t(`status.${lead.status}`)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState message={t("empty")} />
        )}
      </Card>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return <p className="py-6 text-center text-sm text-slate-500">{message}</p>;
}
